import PropTypes from 'prop-types';
import { useState, useEffect } from 'react';
import { Button, useUserInfoContext } from '@spotahome/ui-library';
import {
  clientErrorTracker,
  getPagePath,
  trans,
  useSoyuzExperiment
} from '@spotahome/soyuz/client';

import { createChatChannel } from '@spotahome/marketplace-common/src/graphql/mutations/chat';
import { Mediator } from '@spotahome/soyuz-mediator';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';
import { COMMON_LOGIN_MODAL } from '@spotahome/marketplace-common/src/channels';

import {
  getItem,
  removeItem,
  setItem
} from '@spotahome/ui-library/src/utils/localStorage';

import getGqlClient from '@spotahome/marketplace-common/src/graphql/client';

import KeysIcon from '@spotahome/ui-library/src/tenant/components/icons/KeysIcon';

import './ListingDoubtsLandlord.scss';
import ListingDoubtsLandlordModal from './ListingDoubtsLandlordModal';

const WAITING_FOR_LOGIN = 'waiting';
const LISTING_DOUBTS_LANDLORD_KEY = 'listing-doubts-landlord';

const ListingDoubtsLandlord = ({
  cityId,
  listingId,
  listingAddress,
  listingUrl,
  landlordId,
  countryCode
}) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { isUserLogged } = useUserInfoContext();

  const chatExperiment = useSoyuzExperiment('Chat_Landlord_1');

  const handleModalClose = () => setIsModalOpen(false);

  const openChatInNewTab = channelId => {
    const url = getPagePath('tenant-messages', { query: { id: channelId } });
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const handleOnClickChat = async () => {
    try {
      const createChatChannelMutation = createChatChannel(getGqlClient());

      const variables = {
        listingId
      };

      const response = await createChatChannelMutation(variables);
      const channelId = response?.data?.chatCreateChannel?.channelId;
      if (channelId) {
        openChatInNewTab(channelId);
      }
    } catch (error) {
      clientErrorTracker.error({ error, source: 'ListingDoubtsLandlord-Chat' });
    }
  };

  const handleOnClick = () => {
    if (!isUserLogged) {
      setItem(LISTING_DOUBTS_LANDLORD_KEY, WAITING_FOR_LOGIN);
      Mediator.publish(COMMON_LOGIN_MODAL, {
        initialAction: 'signup',
        renderTitle: action => trans(`${action}.modal.title`),
        renderSubtitle: action => trans(`${action}.modal.subtitle`)
      });

      SoyuzAnalytics.sendGA4Event('open', {
        section: 'listing-doubts-landlord-landlord-sign-up'
      });
    } else {
      setIsModalOpen(true);
      SoyuzAnalytics.sendGA4Event('open', {
        section: 'listing-doubts-landlord-modal'
      });
    }
  };

  useEffect(() => {
    const hasUserUsedLoginModal =
      getItem(LISTING_DOUBTS_LANDLORD_KEY) === WAITING_FOR_LOGIN;

    if (isUserLogged && hasUserUsedLoginModal) {
      setIsModalOpen(true);
    }

    removeItem(LISTING_DOUBTS_LANDLORD_KEY);
  }, [isUserLogged]);

  if (chatExperiment.get('behaviour') === 'variant') {
    return (
      <>
        <ListingDoubtsLandlordModal
          handleClose={handleModalClose}
          isOpen={isModalOpen}
          listingId={listingId}
          listingAddress={listingAddress}
          listingUrl={listingUrl}
          landlordId={landlordId}
          cityId={cityId}
          countryCode={countryCode}
        />
        <div className="listing-doubts-landlord listing-doubts-landlord--variant">
          <div className="listing-doubts-landlord__variant-left">
            <div className="listing-doubts-landlord__title">
              {trans('listing.chat.title')}
            </div>
            <div className="listing-doubts-landlord__text">
              {trans('listing.chat.subtitle')}
            </div>
            <Button
              className="listing-doubts-landlord__cta"
              color="interaction"
              onClick={handleOnClickChat}
              data-e2e="listing-doubts-landlord-cta"
            >
              {trans('listing.chat.cta')}
            </Button>
          </div>
          <div className="listing-doubts-landlord__icon">
            <KeysIcon />
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <ListingDoubtsLandlordModal
        handleClose={handleModalClose}
        isOpen={isModalOpen}
        listingId={listingId}
        listingAddress={listingAddress}
        listingUrl={listingUrl}
        landlordId={landlordId}
        cityId={cityId}
        countryCode={countryCode}
      />
      <div className="listing-doubts-landlord">
        <div className="listing-doubts-landlord__info-wrapper">
          <div>
            <div className="listing-doubts-landlord__title">
              {trans('listing.doubts.landlord.title')}
            </div>
            <div className="listing-doubts-landlord__text">
              {trans('listing.doubts.landlord.text')}
            </div>
          </div>
          <Button
            className="listing-doubts-landlord__cta"
            color="interaction"
            onClick={handleOnClick}
            data-e2e="listing-doubts-landlord-cta"
          >
            {trans('listing.doubts.landlord.cta')}
          </Button>
        </div>
      </div>
    </>
  );
};

ListingDoubtsLandlord.propTypes = {
  listingId: PropTypes.string.isRequired,
  cityId: PropTypes.string.isRequired,
  countryCode: PropTypes.string.isRequired,
  listingAddress: PropTypes.string.isRequired,
  listingUrl: PropTypes.string.isRequired,
  landlordId: PropTypes.string.isRequired
};

export default ListingDoubtsLandlord;
